import type { CountryKey, DateParts } from "./dateIntent";

export const PAKISTAN_CITIES = [
  "Karachi","Lahore","Islamabad","Rawalpindi","Faisalabad","Multan",
  "Peshawar","Quetta","Sialkot","Gujranwala","Hyderabad","Bahawalpur",
  "Sargodha","Mardan",
];

export const INDIA_CITIES = [
  "Delhi","Mumbai","Bangalore","Chennai","Kolkata","Hyderabad",
  "Ahmedabad","Pune","Jaipur","Lucknow","Surat","Kanpur","Nagpur",
  "Indore","Bhopal","Patna","Coimbatore","Kochi","Visakhapatnam",
  "Thiruvananthapuram",
];

export const UAE_CITIES = [
  "Dubai","Abu Dhabi","Sharjah","Ajman","Ras Al Khaimah","Fujairah",
  "Umm Al Quwain","Al Ain",
];

export const SAUDI_CITIES_AR = [
  "الرياض","جدة","مكة","المدينة","الدمام","الخبر","الطائف","تبوك","أبها",
];

export const SAUDI_CITIES_EN = [
  "Riyadh","Jeddah","Mecca","Medina","Dammam","Khobar","Taif","Tabuk","Abha",
];

export const KARATS = ["24K","22K","21K","18K"] as const;
export const UNITS_PK = ["per tola","per 10 gram","per gram"] as const;
export const UNITS_IN = ["per 10 gram","per gram","per tola"] as const;
export const UNITS_AE = ["per gram","per ounce"] as const;
export const UNITS_SA_AR = ["جرام","أوقية"] as const;

export function citiesFor(country: CountryKey): readonly string[] {
  switch (country) {
    case "pakistan": return PAKISTAN_CITIES;
    case "india":    return INDIA_CITIES;
    case "uae":      return UAE_CITIES;
    case "saudi":    return SAUDI_CITIES_AR;
  }
}

export function citiesEnglishFor(country: CountryKey): readonly string[] {
  return country === "saudi" ? SAUDI_CITIES_EN : citiesFor(country);
}

export function countryLabelEN(country: CountryKey): string {
  switch (country) {
    case "pakistan": return "Pakistan";
    case "india":    return "India";
    case "uae":      return "UAE";
    case "saudi":    return "Saudi Arabia";
  }
}

export function citySlug(name: string): string {
  return name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9\-\u0600-\u06FF]/g, "");
}

/**
 * Date-window keyword sentences. Used by TodayDateBlock as 3 secondary
 * lines so we hit yesterday/today/tomorrow/this-week intent without
 * stuffing keywords.
 */
export function dateWindowSentences(country: CountryKey, d: DateParts): string[] {
  const label = countryLabelEN(country);
  return [
    `Yesterday's ${label.toLowerCase()} gold rate (${d.yesterdayLongEN}) is referenced below in the per-tola/per-gram comparison.`,
    `Tomorrow's projected ${label.toLowerCase()} gold rate (${d.tomorrowLongEN}) updates automatically with the next international spot tick.`,
    `This week's ${label.toLowerCase()} gold rate trend from ${d.weekStartLongEN} through ${d.longEN} is visible in the 7-day section.`,
  ];
}

/**
 * Build a deterministic, day-stable list of pill phrases for JumpLinks.
 * Seeded by date.iso so anchors rotate day-to-day (anti-footprint) but
 * stay stable within a day.
 */
export function buildJumpPills(country: CountryKey, d: DateParts) {
  const cities = citiesEnglishFor(country).slice(0, 8);
  const pills: Array<{ id: string; label: string; anchor: string }> = [
    { id: "today",      label: `Today ${d.longEN} Rates`, anchor: `today-${d.slug}` },
    { id: "k24",        label: `24K Gold ${d.longEN}`,    anchor: `karat-24k-${d.slug}` },
    { id: "k22",        label: `22K ${country === "pakistan" ? "Per Tola" : "Per Gram"} Today`, anchor: `karat-22k-${d.slug}` },
    { id: "k21",        label: `21K Per Gram Today`,      anchor: `karat-21k-${d.slug}` },
  ];
  for (const c of cities) {
    pills.push({ id: c, label: `${c} Gold Today`, anchor: `${citySlug(c)}-${d.slug}` });
  }
  return pills;
}